import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bool, func, number, object, string } from 'prop-types';
import { didSortChange, getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import './LandingPageGrid.scss';
import { getColumnIndex } from 'ps-components/AdvancedTable/GenericGrid/GenericGridHelper';
import { getJobMatches } from '../JobMatchDetail/JobMatchDetailRenderer';
import { addSelectedJobs, removeSelectedJobs } from '../LandingPageActions';
import { getDefaultOrField } from '../../../../ps-components/AdvancedTable/GenericGrid/GenericGridCellRenderers';

LandingPageGrid.propTypes = {
  landingPageGrid: object,
  landingPageGridActions: object,
  jobMatchGrid: object,
  jobMatchGridActions: object,
  search: func,
  loading: bool,
  masterAccountId: string,
  readOnlyUser: bool,
  restrictedUser: bool,
  history: object,
  offset: number,
  useJobCodeKey: bool,
  jobCodeKeyText: string,
  searchQuery: string,
  useFacetSearch: bool,
  numericRounding: number,
  percentRounding: number,
  benchmarkYear: number,
  setSelectedBenchmarkYear: func,
  organizationCode: string
};

function LandingPageGrid(props) {
  const selectedJobs = useSelector(state => state.landingPage.landingPageReducer.selectedJobs);
  const dispatch = useDispatch();
  const [sortState, setSortState] = useState(null);
  
  function handleOnRowClick(el, restrictedUser) {
    const id = el?.values?.org_job_id_default;
    if (restrictedUser) {
      props.setSelectedBenchmarkYear(props.benchmarkYear);

      props.history.push(`/market-analysis/market-pricing/${id}/compensation-summary-report`, {
        modal: true,
        modalType: 'COMPENSATION_SUMMARY_REPORT',
        modalProps: { className: 'comp-sum__modal', orgJob: { id } },
        component: 'COMPENSATION_SUMMARY_REPORT'
      });
    } else {
      props.history.push({
        pathname: `/market-analysis/jobs/${id}/pricing`,
        state: { searchQuery: props.searchQuery, offset: props.offset }
      });
    }
  }

  function search() {
    props.search();
  }

  function handleOnSortChange(sort) {
    const sortWithTermId = sort.map(s => {
      const sortedTerm = props.landingPageGrid.terms.find(({ termType, term }) => `${termType}_${term}` === s.id)
      return { ...s, termId: sortedTerm?.termId }
    })
    const sortChanged = sortState !== null && didSortChange(sort, sortState);
    setSortState(sort);
    if (sortChanged) props.landingPageGridActions.onSortChange(sortWithTermId).then(search);
  }

  function isSelectAllChecked() {
    const jobIdIndex = getColumnIndex(props.landingPageGrid, 'org_job_id_default');
    if (jobIdIndex !== -1) {
      const pageJobIds = props.landingPageGrid.data.map(row => row[jobIdIndex]);
      return pageJobIds.every(id => selectedJobs.map(job => job.jobID).includes(id));
    }

    return false;
  }

  return (
    <div className="generic-grid__container landing-page__grid-container">
      <AdvancedTable
        {...props.landingPageGrid.tableProps}
        {...props.landingPageGridActions}
        jobMatchGrid={props.jobMatchGrid}
        jobMatchGridActions={props.jobMatchGridActions}
        benchmarkYear={props.benchmarkYear}
        masterAccountId={props.masterAccountId}
        getDataRow={(row, idx) => {
          return getGenericRow(row, idx, {
            onClick: null,
            useJobCodeKey: props.useJobCodeKey,
            jobCodeKeyText: props.jobCodeKeyText,
            excludeLoadingText: true,
            numericRounding: props.numericRounding,
            percentRounding: props.percentRounding
          });
        }}
        stickyHeader
        useSelectAll={true}
        useSelectAccessor={el => el.values.org_job_id_default}
        isChecked={id => selectedJobs.map(job => job.jobID).includes(id)}
        isAllChecked={isSelectAllChecked()}
        onCheck={(id, el) => {
          const pricingStatus = getDefaultOrField(el.values, 'slot_p');
          if (selectedJobs.map(job => job.jobID).includes(id)) dispatch(removeSelectedJobs([id]));
          else dispatch(addSelectedJobs([{ jobID: id, pricingStatus }]));
        }}
        onSelectAll={() => {
          const isChecked = isSelectAllChecked();
          const jobIdIndex = getColumnIndex(props.landingPageGrid, 'org_job_id_default');
          const slotPIndex = getColumnIndex(props.landingPageGrid, 'slot_p_default');
          if (jobIdIndex === -1) {
            return;
          }
          const uniqueJobIDs = new Set(selectedJobs.map(job => job.jobID));
          const jobIDsToRemove = [];
          const jobsToAdd = [];
          props.landingPageGrid.data.forEach(row => {
            const id = row[jobIdIndex];
            const pricingStatus = row[slotPIndex];
            if (isChecked && uniqueJobIDs.has(id)) {
              jobIDsToRemove.push(id);
            } else if (!isChecked && !uniqueJobIDs.has(id)) {
              jobsToAdd.push({ jobID: id, pricingStatus });
            }
          });
          if (jobIDsToRemove.length) dispatch(removeSelectedJobs(jobIDsToRemove));
          if (jobsToAdd.length) dispatch(addSelectedJobs(jobsToAdd));
        }}
        dataLoading={props.loading}
        allowResize={true}
        allowSorting={true}
        useClientSideSort={false}
        onSortChange={handleOnSortChange}
        loadingText={'Loading Job Data'}
        onRowClick={el => handleOnRowClick(el, props.restrictedUser)}
        resetSortState={[props.useFacetSearch]}
        displayJobMatchDetail={true}
        organizationCode={props.organizationCode}
        getJobMatches={getJobMatches}
      />
    </div>
  );
}

export default LandingPageGrid;
